import React, { useState, useRef, useEffect, useContext, useMemo } from 'react';
import { useApolloClient } from '@apollo/react-hooks';
import { Message as MessageType, froalaEditorRegistry } from './types';
import { Message } from './Message';
import { ChatMenu } from './ChatMenu';
import { ChatHistory } from './ChatHistory';
import { groupChats } from './history';
import { Context as AISearchContext } from './context';
import { generateAIResponse, executeAction, getContextDescription, toHistoryTurn } from './aiService';
import { AI_ICON } from './constants';
import { useQuickPrompts } from './quickPrompts';
import {
  ChatContainer,
  MessagesList,
  TypingIndicator,
  TypingBubble,
  MessageAvatar,
  InputContainer,
  InputWrapper,
  ChatInput,
  SendButton,
  InputHint,
  WelcomeState,
  WelcomeIcon,
  WelcomeTitle,
  WelcomeText,
  Suggestions,
  SuggestionButton,
  ContextIndicator,
  ContextIcon,
  ContextText,
  ContextLabel,
  LifecycleBar,
  LifecycleFlags,
  UnstaleButton,
  SelectionBar,
  BriefButton
} from './styles';

// Canned unstale prompt. The assistant runs get_staleness_report /
// append_article_correction / set_lifecycle_state server-side; nothing is
// written without approval. It asks for a correction NOTE rather than edits
// because the story as published is a legal record and is never rewritten
// (CMS-7401) — the note is attached to it and renders below the body.
const UNSTALE_PROMPT =
  'Explain why this article was flagged, then draft a correction note citing the newer ' +
  'article. Do not attach it or clear the flag until I approve.';

// Canned brief prompt for the article list. The selected articles travel with
// this request only (their text sits in the assistant's prompt), so "these" is
// enough; create_brief links them to the brief as related content.
const BRIEF_PROMPT = 'Create a brief from these articles.';

export const ChatUI: React.FC = () => {
  const {
    activeChat,
    activeChatId,
    createNewChat,
    addMessage,
    isTyping,
    setIsTyping,
    chats,
    pageContext
  } = useContext(AISearchContext);

  const client = useApolloClient();
  const [inputValue, setInputValue] = useState('');
  const [isSending, setIsSending] = useState(false);
  // A message or an action is waiting on the assistant; one request at a time.
  const busy = isSending || isTyping;
  // Rows checked on the article list, offered as the sources of a brief.
  const selectedCount = (pageContext?.pageType === 'article-list' && pageContext?.selectedArticleIds?.length) || 0;
  const selectedArticleIds = selectedCount ? (pageContext?.selectedArticleIds || []).map(String) : [];
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const [newMessageId, setNewMessageId] = useState<string | null>(null);
  const [historyOpen, setHistoryOpen] = useState(false);

  // Chats listed in History (those with a prompt).
  const historyCount = useMemo(
    () => groupChats(chats, '').reduce((n, g) => n + g.chats.length, 0),
    [chats]
  );

  // The menu bar (and so the way back from History) only shows while there are
  // chats; deleting the last one from History must not leave the pane stranded.
  useEffect(() => {
    if (historyOpen && chats.length === 0) setHistoryOpen(false);
  }, [historyOpen, chats.length]);

  // Closing History disables then re-enables the textarea, which drops its
  // focus; put it back so the user can keep typing.
  const wasHistoryOpen = useRef(false);
  useEffect(() => {
    if (wasHistoryOpen.current && !historyOpen) inputRef.current?.focus();
    wasHistoryOpen.current = historyOpen;
  }, [historyOpen]);

  // The textarea is disabled while the assistant works, which drops its focus.
  // Give it back when the request ends, unless the user has moved on to
  // something else on the page meanwhile.
  const wasBusy = useRef(false);
  useEffect(() => {
    const active = document.activeElement;
    if (wasBusy.current && !busy && (!active || active === document.body)) inputRef.current?.focus();
    wasBusy.current = busy;
  }, [busy]);

  // Get messages from active chat
  const messages = activeChat?.messages || [];

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, isTyping]);

  // Clear new message animation after it plays
  useEffect(() => {
    if (newMessageId) {
      const timer = setTimeout(() => setNewMessageId(null), 300);
      return () => clearTimeout(timer);
    }
  }, [newMessageId]);

  // Auto-resize textarea
  const handleInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setInputValue(e.target.value);
    const textarea = e.target;
    textarea.style.height = 'auto';
    textarea.style.height = Math.min(textarea.scrollHeight, 100) + 'px';
  };

  const handleSendMessage = async (messageText?: string, articleIds?: string[]) => {
    const text = messageText || inputValue.trim();
    // The active chat is hidden while History is open; an action may still be running.
    if (!text || busy || historyOpen) return;

    // Create new chat if none exists
    let chatId = activeChatId;
    if (!chatId) {
      chatId = createNewChat();
    }

    const userMessage: MessageType = {
      id: `msg-${Date.now()}`,
      role: 'user',
      content: text,
      timestamp: new Date(),
      // Kept on the turn so later turns (answering "what title?") still know them.
      ...(selectedArticleIds.length ? { selectedArticleIds } : {})
    };

    // Pass chatId explicitly to handle the case where we just created the chat
    addMessage(userMessage, chatId);
    setNewMessageId(userMessage.id);
    setInputValue('');
    if (inputRef.current) {
      inputRef.current.style.height = 'auto';
    }
    setIsSending(true);
    setIsTyping(true);

    try {
      // Prior turns give the assistant conversational memory (it is stateless
      // server-side); the message just added is sent separately.
      const history = messages.map(toHistoryTurn);
      const aiResponse = await generateAIResponse(userMessage.content, pageContext, client, history, {
        articleIds,
        selectedArticleIds
      });

      const assistantMessage: MessageType = {
        id: `msg-${Date.now()}`,
        role: 'assistant',
        content: aiResponse.content,
        reasoning: aiResponse.reasoning,
        toolsUsed: aiResponse.toolsUsed,
        created: aiResponse.created,
        timestamp: new Date(),
        links: aiResponse.links,
        actions: aiResponse.actions,
        searchResults: aiResponse.searchResults
      };

      setIsTyping(false);
      addMessage(assistantMessage, chatId);
      setNewMessageId(assistantMessage.id);
    } catch (error) {
      console.error('Error generating AI response:', error);
      setIsTyping(false);
    } finally {
      setIsSending(false);
    }
  };

  const handleActionClick = async (actionId: string, payload: any) => {
    if (!activeChatId) return;

    // Handle insert-link action — inserts an anchor tag into the active Froala editor.
    // Only insert when the user is actually on an edit page; a stale editor
    // reference must not silently mutate content from elsewhere in the admin.
    if (actionId === 'insert-link' || payload?.type === 'insert-link') {
      let content: string;
      const isEditContext = pageContext?.pageType === 'article-edit' || pageContext?.pageType === 'page-edit';
      const editor = isEditContext ? froalaEditorRegistry.current : null;

      if (editor) {
        try {
          const anchorHtml = `<a href="${payload.url}">${payload.title}</a>`;
          editor.html.insert(anchorHtml, true);
          editor.undo.saveStep();
          editor.events.trigger('contentChanged', [], true);
          content = `Link to "${payload.title}" has been inserted at your cursor position.`;
        } catch (err) {
          console.error('Failed to insert link into editor:', err);
          content = `Could not insert link. The editor may not be active. URL: ${payload.url}`;
        }
      } else {
        // No editor available — copy to clipboard as fallback
        if (navigator.clipboard) {
          navigator.clipboard.writeText(payload.url);
        }
        content = `No active editor found. The URL for "${payload.title}" has been copied to your clipboard instead.`;
      }

      const confirmMessage: MessageType = {
        id: `msg-${Date.now()}`,
        role: 'assistant',
        content,
        timestamp: new Date()
      };
      addMessage(confirmMessage, activeChatId);
      setNewMessageId(confirmMessage.id);
      return;
    }

    // Handle add-related action — will be wired to articleAddArticles mutation later
    if (actionId === 'add-related' || payload?.type === 'add-related') {
      const confirmMessage: MessageType = {
        id: `msg-${Date.now()}`,
        role: 'assistant',
        content: `"${payload.title}" has been added as related content. This association will be saved with the article.`,
        timestamp: new Date()
      };
      addMessage(confirmMessage, activeChatId);
      setNewMessageId(confirmMessage.id);
      return;
    }

    // One request at a time, same as a message.
    if (busy) return;
    setIsTyping(true);

    try {
      const result = await executeAction(payload, client);

      // executeAction can return a string or a rich response object
      const isRichResponse = typeof result !== 'string';

      const confirmationMessage: MessageType = {
        id: `msg-${Date.now()}`,
        role: 'assistant',
        content: isRichResponse ? result.content : result,
        timestamp: new Date(),
        ...(isRichResponse ? {
          created: result.created,
          links: result.links,
          actions: result.actions,
          searchResults: result.searchResults
        } : {})
      };

      setIsTyping(false);
      addMessage(confirmationMessage, activeChatId);
      setNewMessageId(confirmationMessage.id);
    } catch (error) {
      console.error('Error executing action:', error);
      setIsTyping(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const handleSuggestionClick = (suggestion: string) => {
    handleSendMessage(suggestion);
  };

  // Determine if we should show the welcome state
  const showWelcome = messages.length === 0 && !isTyping;

  // Get context description for display
  const contextDescription = getContextDescription(pageContext);

  const isArticleEdit = pageContext?.pageType === 'article-edit';
  // Lifecycle flags travel with the entity context from the article edit
  // view (already on the page from its own query — no extra request).
  const lifecycleFlags: string[] = (isArticleEdit && pageContext?.entity?.lifecycleFlags) || [];
  // Configured on the Content Lifecycle page; the built-ins until then.
  const suggestions = useQuickPrompts(pageContext?.pageType);

  return (
    <ChatContainer>
      {chats.length > 0 && (
        <ChatMenu
          historyOpen={historyOpen}
          onToggleHistory={() => setHistoryOpen((open) => !open)}
          onNewChat={() => {
            createNewChat();
            setHistoryOpen(false);
          }}
          historyCount={historyCount}
        />
      )}

      {pageContext && contextDescription && (
        <ContextIndicator>
          <ContextIcon>
            <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M12 22c5.523 0 10-4.477 10-10S17.523 2 12 2 2 6.477 2 12s4.477 10 10 10z" stroke="currentColor" strokeWidth="2"/>
              <path d="M12 16v-4M12 8h.01" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
            </svg>
          </ContextIcon>
          <ContextText>
            <ContextLabel>Context:</ContextLabel>
            {contextDescription}
          </ContextText>
        </ContextIndicator>
      )}

      {lifecycleFlags.length > 0 && (
        <LifecycleBar>
          <LifecycleFlags>
            <strong>Flagged:</strong> {lifecycleFlags.join(', ')}
          </LifecycleFlags>
          <UnstaleButton
            disabled={busy || historyOpen}
            onClick={() => handleSendMessage(UNSTALE_PROMPT)}
            title="Ask the assistant to explain the flag and propose corrections"
          >
            Unstale
          </UnstaleButton>
        </LifecycleBar>
      )}

      {selectedCount > 0 && (
        <SelectionBar>
          <LifecycleFlags>
            <strong>{selectedCount}</strong> article{selectedCount === 1 ? '' : 's'} selected
          </LifecycleFlags>
          <BriefButton
            disabled={busy || historyOpen}
            onClick={() => handleSendMessage(BRIEF_PROMPT, selectedArticleIds)}
            title="Draft an unpublished brief from the selected articles"
          >
            Create brief
          </BriefButton>
        </SelectionBar>
      )}

      {historyOpen && (
        <ChatHistory onSelect={() => setHistoryOpen(false)} onClose={() => setHistoryOpen(false)} />
      )}

      <MessagesList hidden={historyOpen}>
        {showWelcome ? (
          <WelcomeState>
            <WelcomeIcon src={AI_ICON} alt="AI Assistant" />
            <WelcomeTitle>How can I help you today?</WelcomeTitle>
            <WelcomeText>
              {isArticleEdit
                ? 'I can find related content, suggest tags, and help you cross-link articles — all without leaving the editor.'
                : 'I can help you find related content, discover articles by topic, and perform quick actions across your CMS.'}
            </WelcomeText>
            <Suggestions>
              {suggestions.map((suggestion) => (
                <SuggestionButton
                  key={suggestion.label}
                  onClick={() => handleSuggestionClick(suggestion.prompt)}
                >
                  {suggestion.label}
                </SuggestionButton>
              ))}
            </Suggestions>
          </WelcomeState>
        ) : (
          <>
            {messages.map((message) => (
              <Message
                key={message.id}
                message={message}
                isNew={message.id === newMessageId}
                onActionClick={handleActionClick}
              />
            ))}
            {isTyping && (
              <TypingIndicator>
                <MessageAvatar src={AI_ICON} alt="AI" />
                <TypingBubble>
                  <span />
                  <span />
                  <span />
                </TypingBubble>
              </TypingIndicator>
            )}
          </>
        )}
        <div ref={messagesEndRef} />
      </MessagesList>

      <InputContainer>
        <InputWrapper>
          <ChatInput
            ref={inputRef}
            value={inputValue}
            onChange={handleInputChange}
            onKeyDown={handleKeyDown}
            placeholder={isArticleEdit ? 'Search for related content or ask a question...' : 'Ask me anything...'}
            rows={1}
            disabled={busy || historyOpen}
          />
          <SendButton
            onClick={() => handleSendMessage()}
            disabled={!inputValue.trim() || busy || historyOpen}
            title="Send message"
          >
            <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M22 2L11 13M22 2l-7 20-4-9-9-4 20-7z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </SendButton>
        </InputWrapper>
        <InputHint>Press Enter to send, Shift+Enter for new line</InputHint>
      </InputContainer>
    </ChatContainer>
  );
};
